import { FC, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Settings, Plus } from "lucide-react"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import DeviceFilters from "./DeviceFilters"
import DeviceList from "./DeviceList"
import AssignDeviceDialog from "./AssignDeviceDialog"
import { useGetDevicePage } from "@/hooks/api/use-get-device-page"
import { LoadingSpinner } from "./ui/loading-spinner"

interface CustomerDevicesProps {
  customerId: string
  councilId: string
}

const CustomerDevices: FC<CustomerDevicesProps> = ({ customerId, councilId }) => {
  const [deviceFilter, setDeviceFilter] = useState("all")
  const [deviceSearch, setDeviceSearch] = useState("")
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("all")
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false)

  const deviceType = deviceTypeFilter === "all" ? undefined : deviceTypeFilter

  const { data, isLoading, error } = useGetDevicePage(
    councilId,
    undefined, // search is client-side only; backend doesn't support name-based search
    undefined, // alarms/normal filter is client-side only, not a backend status value
    parseInt(customerId),
    deviceType,
  )

  const devices =
    data?.pages.flatMap((page) =>
      page.devices.map((device) => ({
        id: device.id || "",
        platform: device.platform || "",
        name: device.name || null,
        serialNumber: device.serialNumber || "",
        type: device.type || null,
        status: device.status || "UNKNOWN",
        location: device.location || null,
        lastUpdated: device.lastUpdated || null,
        batteryLevel: device.batteryLevel || 0,
        signalStrength: device.signalStrength || 0,
        leakAlarms: device.leakAlarms || 0,
      })),
    ) ?? []

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center min-h-[688px]">
          <LoadingSpinner />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-red-500">Error loading devices: {error.message}</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="size-5" />
              Customer Devices
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              All devices associated with this customer account
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAssignDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="size-4" />
              Assign Devices
            </Button>
            <Badge variant="secondary">
              {devices.length} Device{devices.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 mb-4">
          <DeviceFilters
            deviceSearch={deviceSearch}
            setDeviceSearch={setDeviceSearch}
            deviceTypeFilter={deviceTypeFilter}
            setDeviceTypeFilter={setDeviceTypeFilter}
            deviceFilter={deviceFilter}
            setDeviceFilter={setDeviceFilter}
          />
        </div>
        <DeviceList
          devices={devices}
          deviceSearch={deviceSearch}
          deviceFilter={deviceFilter}
          deviceTypeFilter={deviceTypeFilter}
        />
      </CardContent>

      <AssignDeviceDialog
        open={isAssignDialogOpen}
        onOpenChange={setIsAssignDialogOpen}
        councilId={councilId}
        customerId={parseInt(customerId)}
        assignedDevices={devices.map((device) => ({
          platformId: device.id,
          platform: device.platform,
        }))}
      />
    </Card>
  )
}

export default CustomerDevices
